import React from 'react';
import {
  EyeIcon,
  ExclamationTriangleIcon,
  BeakerIcon,
  UserGroupIcon,
  CameraIcon,
} from '@heroicons/react/24/outline';
import { useSessionSummaryQuery } from '../../store/api/localApi';

interface Props {
  sessionId: string;
}

interface Stat {
  key: string;
  testid: string;
  label: string;
  value: number;
  icon: React.ReactNode;
  /** Amber when there is at least one incident — the one count that should
   *  draw the eye. Everything else stays neutral. */
  emphasis?: boolean;
  /** Pink accent — moments read as the warm "share" against the otherwise
   *  blue/neutral strip, so they always carry their own colour (not gated on
   *  a count the way `emphasis` is). */
  warm?: boolean;
}

/**
 * A glanceable roll-up of what has been recorded against a session — how many
 * children are present, how many observations and incidents staff logged, and
 * how many medication doses were given. Live while the session runs (RTK Query
 * refetches when any of those are recorded) and stays on the ended view as a
 * recap. Staff-only: the endpoint is gated to staff, and this only renders on
 * the staff RunSession page.
 */
const SessionSummaryStrip: React.FC<Props> = ({ sessionId }) => {
  const { data, isLoading } = useSessionSummaryQuery(sessionId);

  if (isLoading || !data) return null;

  const stats: Stat[] = [
    {
      key: 'present',
      testid: 'session-summary-present',
      label: 'present',
      value: data.presentCount,
      icon: <UserGroupIcon className="w-5 h-5" />,
    },
    {
      key: 'observations',
      testid: 'session-summary-observations',
      label: data.observationCount === 1 ? 'observation' : 'observations',
      value: data.observationCount,
      icon: <EyeIcon className="w-5 h-5" />,
    },
    {
      key: 'incidents',
      testid: 'session-summary-incidents',
      label: data.incidentCount === 1 ? 'incident' : 'incidents',
      value: data.incidentCount,
      icon: <ExclamationTriangleIcon className="w-5 h-5" />,
      emphasis: data.incidentCount > 0,
    },
    {
      key: 'moments',
      testid: 'session-summary-moments',
      label: data.momentCount === 1 ? 'moment' : 'moments',
      value: data.momentCount,
      icon: <CameraIcon className="w-5 h-5" />,
      warm: true,
    },
    {
      key: 'meds',
      testid: 'session-summary-meds',
      label: data.medsGiven === 1 ? 'dose given' : 'doses given',
      value: data.medsGiven,
      icon: <BeakerIcon className="w-5 h-5" />,
    },
  ];

  return (
    <div data-testid="session-summary-strip" className="grid grid-cols-2 sm:grid-cols-5 gap-2">
      {stats.map((s) => (
        <div
          key={s.key}
          data-testid={s.testid}
          className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 ${
            s.emphasis
              ? 'border-amber-300 bg-amber-50 text-amber-900'
              : s.warm
              ? 'border-pink-200 bg-pink-50 text-pink-900'
              : 'border-slate-200 bg-white text-slate-700'
          }`}
        >
          <span
            className={s.emphasis ? 'text-amber-600' : s.warm ? 'text-pink-500' : 'text-slate-400'}
          >
            {s.icon}
          </span>
          <span className="flex items-baseline gap-1.5">
            <span data-testid={`${s.testid}-count`} className="text-xl font-semibold tabular-nums">
              {s.value}
            </span>
            <span className="text-xs">{s.label}</span>
          </span>
        </div>
      ))}
    </div>
  );
};

export default SessionSummaryStrip;
